import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl, mxn, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Search, Package, Tag, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function SearchSurplus() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [priceInput, setPriceInput] = useState("");

  const fetchItems = () => {
    const params = { is_surplus: true };
    if (q) params.q = q;
    api.get("/assets", { params }).then(({ data }) => setItems(data));
  };

  useEffect(() => {
    const t = setTimeout(fetchItems, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const savePrice = async () => {
    try {
      const v = parseFloat(priceInput);
      if (isNaN(v) || v < 0) {
        toast.error("Ingresa un precio válido");
        return;
      }
      await api.patch(`/assets/${editing.id}/price`, { internal_price: v });
      toast.success("Precio actualizado");
      setEditing(null);
      fetchItems();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo actualizar el precio");
    }
  };

  const canEditPrice = user?.role === "cost_dept" || user?.role === "admin";

  return (
    <div className="space-y-6" data-testid="surplus-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C6A7D]">Función 03</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0A2540] tracking-tighter mt-1">Material sobrante.</h1>
          <p className="mt-2 text-sm text-[#5C6A7D] max-w-2xl">Materiales y activos sobrantes de todas las obras. Precios internos asignados por Costos.</p>
        </div>
        <div className="relative w-full sm:w-80">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6A7D]" />
          <Input placeholder="Buscar sobrantes…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-sm border-[#E2E8F0] h-10" data-testid="surplus-search-input" />
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-sm p-16 text-center text-sm text-[#5C6A7D]" data-testid="surplus-empty">
          <Package className="mx-auto mb-3" size={28} strokeWidth={1.5} />
          Aún no hay activos sobrantes. Sube un activo y márcalo como sobrante para listarlo aquí.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="surplus-grid">
          {items.map((a) => (
            <div key={a.id} className="bg-white border border-[#E2E8F0] rounded-sm overflow-hidden hover:border-[#0A2540]/30 transition-colors group">
              <Link to={`/assets/${a.id}`} className="block">
                <div className="aspect-[4/3] bg-[#EAF0F6] overflow-hidden">
                  {a.photos?.[0] ? (
                    <img src={fileUrl(a.photos[0])} alt={a.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-300" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#5C6A7D]"><Package size={28} strokeWidth={1.5} /></div>
                  )}
                </div>
              </Link>
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C6A7D]">{a.category}</div>
                    <Link to={`/assets/${a.id}`} className="font-display text-lg font-bold text-[#0A2540] hover:underline block mt-0.5 leading-tight">{a.name}</Link>
                  </div>
                  <span className="inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] font-bold rounded-sm bg-[#16A34A] text-white">Sobrante</span>
                </div>
                <p className="text-sm text-[#5C6A7D] mt-2 line-clamp-2 min-h-[2.5rem]">{a.description || "Sin descripción."}</p>
                <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                  <div><div className="text-[10px] uppercase tracking-[0.15em] text-[#5C6A7D]">Cantidad</div><div className="font-mono text-[#0A2540] font-semibold">{a.quantity} {a.unit}</div></div>
                  <div><div className="text-[10px] uppercase tracking-[0.15em] text-[#5C6A7D]">Ubicación</div><div className="text-[#0A2540] font-semibold truncate">{a.project_name}</div></div>
                </div>
                <div className="border-t border-[#E2E8F0] mt-4 pt-3 flex items-end justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[#5C6A7D]">Precio interno</div>
                    <div className="font-display text-xl font-extrabold text-[#0A2540]" data-testid={`price-${a.id}`}>{mxn(a.internal_price)}</div>
                  </div>
                  {canEditPrice && (
                    <Button
                      size="sm" variant="outline"
                      className="rounded-sm border-[#E2E8F0] text-[#0A2540] hover:bg-[#EAF0F6]"
                      onClick={() => { setEditing(a); setPriceInput(String(a.internal_price || 0)); }}
                      data-testid={`edit-price-${a.id}`}
                    >
                      <Pencil size={12} className="mr-1" /> Precio
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Definir precio interno</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Precio en MXN</Label>
            <div className="flex items-center gap-2">
              <Tag size={16} className="text-[#5C6A7D]" />
              <Input type="number" min={0} step="0.01" value={priceInput} onChange={(e) => setPriceInput(e.target.value)} className="rounded-sm h-11" data-testid="price-input" />
            </div>
            <p className="text-xs text-[#5C6A7D]">Para: <span className="font-semibold text-[#0A2540]">{editing?.name}</span></p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-sm">Cancelar</Button>
            <Button onClick={savePrice} className="bg-[#0A2540] hover:bg-[#173B63] text-white rounded-sm" data-testid="save-price-button">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
